import type {
  ConstructionEstimatorAuthorization,
  ConstructionPricingMode,
  QuoteCalculationChargeType,
  QuoteCalculationPolicy,
  QuoteCalculationTaxRule,
} from '@/lib/commerce';
import {
  listApiGatewayKeys,
  type ApiGatewayKeyRecordResponse,
} from '@/lib/api-gateway-management';

export const constructionEstimatorSubmitPermission = 'tenant.quotes.construction-estimate.submit';

export type QuotePolicyPricingMode = 'automatic' | ConstructionPricingMode;

export interface QuotePolicyTaxRuleFormValue {
  rowId: string;
  ruleId: string;
  name: string;
  ratePercent: string;
  categoriesText: string;
  chargeTypes: QuoteCalculationChargeType[];
}

export interface PricebookQuotePolicyFormValue {
  constructionPricingMode: QuotePolicyPricingMode;
  constructionCustomerMarkupPercent: string;
  constructionEstimatorAuthorization: ConstructionEstimatorAuthorization;
  constructionEstimatorServiceAccountIds: string[];
  taxRules: QuotePolicyTaxRuleFormValue[];
  constructionTaxable: boolean;
  constructionTaxCategory: string;
}

export interface QuotePolicyEstimatorOption {
  serviceAccountId: string;
  label: string;
  credentialCount: number;
  credentialLabels: string[];
  isSelected: boolean;
  isSelectable: boolean;
  status: 'active' | 'disabled' | 'revoked' | 'unresolved';
  statusLabel: string;
}

export interface QuotePolicyEstimatorOptionsState {
  status: 'loaded' | 'unavailable' | 'error';
  options: QuotePolicyEstimatorOption[];
  selectedServiceAccountIds: string[];
  unresolvedSelectedServiceAccountIds: string[];
  isReadOnly: boolean;
  message?: string;
}

export interface QuotePolicyValidationIssue {
  id: string;
  destination: 'quote_policy';
  field: string;
  message: string;
  rowId?: string;
}

export interface QuotePolicyReviewChangeItem {
  id: string;
  label: string;
  beforeValue?: string;
  afterValue?: string;
  destination: 'quote_policy';
  field: string;
  rowId?: string;
}

export interface QuotePolicyReviewChangeGroup {
  id: 'quote_policy';
  label: string;
  count: number;
  items: QuotePolicyReviewChangeItem[];
}

export interface QuotePolicyReadinessLink {
  id: string;
  label: string;
  href: string;
  isReady: boolean;
  detail: string;
}

export interface QuotePolicyViewModel {
  destination: 'quote_policy';
  value: PricebookQuotePolicyFormValue;
  validationIssues: QuotePolicyValidationIssue[];
  estimatorOptions: QuotePolicyEstimatorOptionsState;
  readinessLinks: QuotePolicyReadinessLink[];
  markupPreviewLabel: string;
  isDirty: boolean;
  isReadOnly: boolean;
}

function uniqueTrimmed(values: string[]) {
  return Array.from(new Set(values.map(value => value.trim()).filter(Boolean))).sort();
}

function normalizedTokens(value: string) {
  return uniqueTrimmed(value.split(/[\s,]+/).map(token => token.toLowerCase()));
}

function decimalToBps(value: string, label: string, maximum?: number) {
  const normalized = value.trim();
  if (!normalized) return 0;
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(normalized);
  if (!match) throw new Error(`${label} must be a non-negative percentage with no more than 2 decimal places.`);
  const whole = Number(match[1]);
  const fraction = Number((match[2] || '').padEnd(2, '0'));
  const bps = whole * 100 + fraction;
  if (!Number.isSafeInteger(bps)) throw new Error(`${label} is too large.`);
  if (maximum != null && bps > maximum * 100) {
    throw new Error(`${label} must be between 0 and ${maximum}.`);
  }
  return bps;
}

function bpsToPercent(value?: number | null) {
  if (!value) return '';
  const whole = Math.trunc(value / 100);
  const fraction = String(value % 100).padStart(2, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : String(whole);
}

function quotePolicyIsEmpty(form: PricebookQuotePolicyFormValue) {
  return form.constructionPricingMode === 'automatic'
    && !form.constructionCustomerMarkupPercent.trim()
    && form.constructionEstimatorAuthorization === 'any_authorized'
    && form.taxRules.length === 0
    && !form.constructionTaxable;
}

export function createDefaultQuotePolicyFormValue(): PricebookQuotePolicyFormValue {
  return {
    constructionPricingMode: 'automatic',
    constructionCustomerMarkupPercent: '',
    constructionEstimatorAuthorization: 'any_authorized',
    constructionEstimatorServiceAccountIds: [],
    taxRules: [],
    constructionTaxable: false,
    constructionTaxCategory: '',
  };
}

export function hydrateQuotePolicy(policy?: QuoteCalculationPolicy | null): PricebookQuotePolicyFormValue {
  if (!policy) return createDefaultQuotePolicyFormValue();
  return {
    constructionPricingMode: policy.construction_pricing_mode || 'automatic',
    constructionCustomerMarkupPercent: bpsToPercent(policy.construction_customer_markup_bps),
    constructionEstimatorAuthorization: policy.construction_estimator_authorization || 'any_authorized',
    constructionEstimatorServiceAccountIds: uniqueTrimmed(policy.construction_estimator_service_account_ids || []),
    taxRules: (policy.tax_rules || []).map((rule, index) => ({
      rowId: rule.rule_id || `tax-rule-${index + 1}`,
      ruleId: rule.rule_id,
      name: rule.name,
      ratePercent: bpsToPercent(rule.rate_bps),
      categoriesText: rule.categories.join(', '),
      chargeTypes: [...(rule.charge_types || [])],
    })),
    constructionTaxable: Boolean(policy.construction_taxable),
    constructionTaxCategory: policy.construction_tax_category || '',
  };
}

function serializeTaxRule(rule: QuotePolicyTaxRuleFormValue): QuoteCalculationTaxRule {
  return {
    rule_id: rule.ruleId.trim(),
    name: rule.name.trim(),
    rate_bps: decimalToBps(rule.ratePercent, 'Tax rate', 100),
    categories: normalizedTokens(rule.categoriesText),
    ...(rule.chargeTypes.length > 0 ? { charge_types: Array.from(new Set(rule.chargeTypes)) } : {}),
  };
}

export function serializeQuotePolicy(
  form?: PricebookQuotePolicyFormValue | null,
): QuoteCalculationPolicy | undefined {
  if (!form || quotePolicyIsEmpty(form)) return undefined;
  const selectedIds = form.constructionEstimatorAuthorization === 'selected'
    ? uniqueTrimmed(form.constructionEstimatorServiceAccountIds)
    : [];
  return {
    ...(form.constructionPricingMode === 'automatic'
      ? {}
      : { construction_pricing_mode: form.constructionPricingMode }),
    ...(form.constructionCustomerMarkupPercent.trim()
      ? { construction_customer_markup_bps: decimalToBps(form.constructionCustomerMarkupPercent, 'Customer markup') }
      : {}),
    construction_estimator_authorization: form.constructionEstimatorAuthorization,
    ...(selectedIds.length > 0 ? { construction_estimator_service_account_ids: selectedIds } : {}),
    ...(form.taxRules.length > 0 ? { tax_rules: form.taxRules.map(serializeTaxRule) } : {}),
    ...(form.constructionTaxable
      ? {
          construction_taxable: true,
          construction_tax_category: form.constructionTaxCategory.trim().toLowerCase(),
        }
      : {}),
  };
}

function issue(field: string, message: string, rowId?: string): QuotePolicyValidationIssue {
  return { id: field, destination: 'quote_policy', field, message, rowId };
}

export function validateQuotePolicy(
  form: PricebookQuotePolicyFormValue,
  estimatorOptions?: QuotePolicyEstimatorOptionsState,
): QuotePolicyValidationIssue[] {
  const issues: QuotePolicyValidationIssue[] = [];
  try {
    decimalToBps(form.constructionCustomerMarkupPercent, 'Customer markup');
  } catch (error) {
    issues.push(issue('quotePolicy.constructionCustomerMarkupPercent', (error as Error).message));
  }

  const selectedIds = uniqueTrimmed(form.constructionEstimatorServiceAccountIds);
  if (form.constructionEstimatorAuthorization === 'selected') {
    if (selectedIds.length === 0) {
      issues.push(issue(
        'quotePolicy.constructionEstimatorServiceAccountIds',
        'Select at least one authorized construction estimator.',
      ));
    }
    if (estimatorOptions?.status === 'loaded') {
      const optionById = new Map(estimatorOptions.options.map(option => [option.serviceAccountId, option]));
      selectedIds.forEach(serviceAccountId => {
        const option = optionById.get(serviceAccountId);
        if (!option || !option.isSelectable) {
          issues.push(issue(
            `quotePolicy.constructionEstimatorServiceAccountIds.${serviceAccountId}`,
            `${option?.label || serviceAccountId} is disabled, revoked, or no longer authorized.`,
          ));
        }
      });
    }
  }

  const seenRuleIds = new Set<string>();
  form.taxRules.forEach((rule, index) => {
    const prefix = `quotePolicy.taxRules.${index}`;
    const ruleId = rule.ruleId.trim();
    if (!ruleId) issues.push(issue(`${prefix}.ruleId`, 'Tax rule ID is required.', rule.rowId));
    if (ruleId && seenRuleIds.has(ruleId)) {
      issues.push(issue(`${prefix}.ruleId`, `Tax rule ID ${ruleId} is duplicated.`, rule.rowId));
    }
    seenRuleIds.add(ruleId);
    if (!rule.name.trim()) issues.push(issue(`${prefix}.name`, 'Tax rule name is required.', rule.rowId));
    try {
      decimalToBps(rule.ratePercent, 'Tax rate', 100);
    } catch (error) {
      issues.push(issue(`${prefix}.ratePercent`, (error as Error).message, rule.rowId));
    }
    if (normalizedTokens(rule.categoriesText).length === 0) {
      issues.push(issue(`${prefix}.categoriesText`, 'Add at least one tax category.', rule.rowId));
    }
  });

  if (form.constructionTaxable && !form.constructionTaxCategory.trim()) {
    issues.push(issue(
      'quotePolicy.constructionTaxCategory',
      'Construction tax category is required when construction is taxable.',
    ));
  }
  return issues;
}

function credentialIsActive(key: ApiGatewayKeyRecordResponse) {
  return key.status.toLowerCase() === 'active'
    && (!key.linked_principal_status || key.linked_principal_status.toLowerCase() === 'active');
}

function inactiveStatus(keys: ApiGatewayKeyRecordResponse[]): QuotePolicyEstimatorOption['status'] {
  if (keys.some(key => key.status.toLowerCase() === 'revoked')) return 'revoked';
  return 'disabled';
}

export function buildQuotePolicyEstimatorOptions(
  keys: ApiGatewayKeyRecordResponse[],
  selectedServiceAccountIds: string[],
): QuotePolicyEstimatorOptionsState {
  const selectedIds = uniqueTrimmed(selectedServiceAccountIds);
  const eligibleKeys = keys.filter(key => (
    key.service_account_id
    && key.permissions.includes(constructionEstimatorSubmitPermission)
  ));
  const grouped = new Map<string, ApiGatewayKeyRecordResponse[]>();
  eligibleKeys.forEach(key => {
    const serviceAccountId = key.service_account_id as string;
    grouped.set(serviceAccountId, [...(grouped.get(serviceAccountId) || []), key]);
  });
  const options = Array.from(grouped.entries()).map(([serviceAccountId, credentials]) => {
    const isSelectable = credentials.some(credentialIsActive);
    const status = isSelectable ? 'active' as const : inactiveStatus(credentials);
    const credentialLabels = credentials.map(key => key.display_name).sort();
    return {
      serviceAccountId,
      label: credentialLabels[0] || serviceAccountId,
      credentialCount: credentials.length,
      credentialLabels,
      isSelected: selectedIds.includes(serviceAccountId),
      isSelectable,
      status,
      statusLabel: isSelectable ? 'Active' : status === 'revoked' ? 'Revoked' : 'Disabled',
    } satisfies QuotePolicyEstimatorOption;
  });
  const unresolvedSelectedServiceAccountIds = selectedIds.filter(id => !grouped.has(id));
  unresolvedSelectedServiceAccountIds.forEach(serviceAccountId => options.push({
    serviceAccountId,
    label: serviceAccountId,
    credentialCount: 0,
    credentialLabels: [],
    isSelected: true,
    isSelectable: false,
    status: 'unresolved',
    statusLabel: 'No longer authorized',
  }));
  options.sort((left, right) => left.label.localeCompare(right.label));
  return {
    status: 'loaded',
    options,
    selectedServiceAccountIds: selectedIds,
    unresolvedSelectedServiceAccountIds,
    isReadOnly: false,
  };
}

function inaccessibleEstimatorState(
  status: 'unavailable' | 'error',
  selectedIds: string[],
  message: string,
): QuotePolicyEstimatorOptionsState {
  const normalizedIds = uniqueTrimmed(selectedIds);
  return {
    status,
    options: normalizedIds.map(serviceAccountId => ({
      serviceAccountId,
      label: serviceAccountId,
      credentialCount: 0,
      credentialLabels: [],
      isSelected: true,
      isSelectable: false,
      status: 'unresolved',
      statusLabel: 'Unresolved',
    })),
    selectedServiceAccountIds: normalizedIds,
    unresolvedSelectedServiceAccountIds: normalizedIds,
    isReadOnly: true,
    message,
  };
}

export async function loadQuotePolicyEstimatorOptions(options: {
  selectedServiceAccountIds: string[];
  canReadAutomationKeys: boolean;
  listKeys?: () => Promise<ApiGatewayKeyRecordResponse[]>;
}): Promise<QuotePolicyEstimatorOptionsState> {
  if (!options.canReadAutomationKeys) {
    return inaccessibleEstimatorState(
      'unavailable',
      options.selectedServiceAccountIds,
      'Automation-key access is unavailable. Existing estimator selections are preserved read-only.',
    );
  }
  try {
    const keys = await (options.listKeys || listApiGatewayKeys)();
    return buildQuotePolicyEstimatorOptions(keys, options.selectedServiceAccountIds);
  } catch {
    return inaccessibleEstimatorState(
      'error',
      options.selectedServiceAccountIds,
      'Estimator credentials could not be loaded. Existing selections are preserved read-only.',
    );
  }
}

export function buildQuotePolicyReviewChangeGroup(
  form?: PricebookQuotePolicyFormValue | null,
  initialForm?: PricebookQuotePolicyFormValue | null,
): QuotePolicyReviewChangeGroup {
  const before = initialForm || createDefaultQuotePolicyFormValue();
  const after = form || createDefaultQuotePolicyFormValue();
  const estimatorSummary = (value: PricebookQuotePolicyFormValue) => value.constructionEstimatorAuthorization === 'selected'
    ? `${value.constructionEstimatorServiceAccountIds.length} selected: ${uniqueTrimmed(value.constructionEstimatorServiceAccountIds).join(', ') || 'None'}`
    : 'Any authorized';
  const taxRuleSummary = (rules: QuotePolicyTaxRuleFormValue[]) => rules.map(rule => {
    const identity = rule.name.trim() || rule.ruleId.trim() || 'Unnamed rule';
    const categories = normalizedTokens(rule.categoriesText).join(', ') || 'all categories';
    const chargeTypes = uniqueTrimmed(rule.chargeTypes).join(', ') || 'all charge types';
    return `${identity}: ${rule.ratePercent.trim() || '0'}%; ${categories}; ${chargeTypes}`;
  }).join(' | ') || 'None';
  const summaries = [
    { id: 'mode', label: 'Construction mode', field: 'quotePolicy.constructionPricingMode', before: before.constructionPricingMode, after: after.constructionPricingMode },
    { id: 'markup', label: 'Customer markup', field: 'quotePolicy.constructionCustomerMarkupPercent', before: `${before.constructionCustomerMarkupPercent || '0'}%`, after: `${after.constructionCustomerMarkupPercent || '0'}%` },
    {
      id: 'estimators', label: 'Estimator authorization', field: 'quotePolicy.constructionEstimatorAuthorization',
      before: estimatorSummary(before),
      after: estimatorSummary(after),
    },
    {
      id: 'construction-tax', label: 'Construction tax', field: 'quotePolicy.constructionTaxable',
      before: before.constructionTaxable ? `Taxable: ${before.constructionTaxCategory}` : 'Not taxable',
      after: after.constructionTaxable ? `Taxable: ${after.constructionTaxCategory}` : 'Not taxable',
    },
    {
      id: 'tax-rules', label: 'Tax rules', field: 'quotePolicy.taxRules',
      before: taxRuleSummary(before.taxRules),
      after: taxRuleSummary(after.taxRules),
    },
  ];
  const items: QuotePolicyReviewChangeItem[] = summaries
    .filter(summary => summary.before !== summary.after)
    .map(summary => ({
      id: `quote-policy-${summary.id}`,
      label: summary.label,
      beforeValue: summary.before,
      afterValue: summary.after,
      destination: 'quote_policy',
      field: summary.field,
    }));
  return { id: 'quote_policy', label: 'Quote policy changes', count: items.length, items };
}

export function buildQuotePolicyViewModel(options: {
  value: PricebookQuotePolicyFormValue;
  initialValue?: PricebookQuotePolicyFormValue | null;
  estimatorOptions: QuotePolicyEstimatorOptionsState;
  readinessLinks?: QuotePolicyReadinessLink[];
}): QuotePolicyViewModel {
  const serialized = serializeQuotePolicy(options.value);
  const initialSerialized = serializeQuotePolicy(options.initialValue);
  const markupBps = serialized?.construction_customer_markup_bps || 0;
  return {
    destination: 'quote_policy',
    value: options.value,
    validationIssues: validateQuotePolicy(options.value, options.estimatorOptions),
    estimatorOptions: options.estimatorOptions,
    readinessLinks: options.readinessLinks || [],
    markupPreviewLabel: markupBps > 0
      ? `A $1,000 construction cost becomes $${(1_000 * (10_000 + markupBps) / 10_000).toFixed(2)}.`
      : 'Construction cost is passed through without customer markup.',
    isDirty: JSON.stringify(serialized) !== JSON.stringify(initialSerialized),
    isReadOnly: options.estimatorOptions.isReadOnly,
  };
}
